import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import trainService from '../services/trainService';
import TrainCard from '../components/TrainCard';
import { setSearch, setResults, setLoading } from '../store/store';
import { useNavigate } from 'react-router-dom';
import stationList from '../utils/stationsList';

const SearchPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [journeyDate, setJourneyDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);

  const results = useSelector(state => state.search.results);
  const loading = useSelector(state => state.search.loading);

  const stations = stationList;

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!source || !destination || !journeyDate) {
      alert('Please fill all fields');
      return;
    }

    if (source === destination) {
      alert('Source and destination cannot be the same');
      return;
    }

    dispatch(setLoading(true));
    setHasSearched(true);

    try {
      const trains = await trainService.searchTrains(source, destination, journeyDate);
      dispatch(setResults(trains));
      dispatch(setSearch({ source, destination, journeyDate, passengers }));
    } catch (error) {
      console.error('Search error:', error);
      alert('Error searching trains: ' + error.message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSelectTrain = (train) => {
    navigate(`/seat-selection/${train.trainId}`, {
      state: { train, passengers, journeyDate },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Bar */}
      <div className="bg-primary text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-6">Search Trains</h1>

          <form onSubmit={handleSearch} className="bg-white text-gray-900 p-6 rounded-lg shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold mb-2">From</label>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  required
                >
                  <option value="">Select station</option>
                  {stations.map(station => (
                    <option key={station} value={station}>{station}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">To</label>
                <select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  required
                >
                  <option value="">Select station</option>
                  {stations.map(station => (
                    <option key={station} value={station}>{station}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Date</label>
                <input
                  type="date"
                  value={journeyDate}
                  onChange={(e) => setJourneyDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Passengers</label>
                <input
                  type="number"
                  value={passengers}
                  onChange={(e) => setPassengers(parseInt(e.target.value))}
                  min="1"
                  max="9"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full bg-accent text-gray-900 py-2 rounded-lg hover:bg-yellow-500 font-semibold transition"
                  disabled={loading}
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : results.length > 0 ? (
          <div>
            <h2 className="text-2xl font-bold mb-6">
              Found {results.length} train{results.length !== 1 ? 's' : ''}
            </h2>
            <div className="space-y-4">
              {results.map(train => (
                <TrainCard
                  key={train.trainId}
                  train={train}
                  onSelect={handleSelectTrain}
                />
              ))}
            </div>
          </div>
        ) : hasSearched ? (
          <div className="text-center py-12 text-gray-600">
            <p className="text-lg">No trains found. Please try different criteria.</p>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-600">
            <p className="text-lg">Search for trains to see available journeys.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
